'use client';

import { useSearchParams } from 'next/navigation';
import { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo, memo } from 'react';
import DatePicker from 'react-datepicker';
// react-datepicker CSS imported in globals.css
import {
  SportsEventsClient,
  type FilterOptions,
  type FilterOptionItem,
  type SearchEventsParams,
} from '@bip/api-client';
import { CONCERTS_API_BASE_URL } from '../config';
import { useCurrency } from '../../_components/CurrencyProvider';

/**
 * Which listing page the filters are displayed on.
 * Controls which filters are visible per the story matrix.
 */
export type ListingPageType = 'search' | 'team' | 'venue' | 'city';

interface EventsFiltersProps {
  basePath?: string;
  title?: string;
  total?: number;
  pageType?: ListingPageType;
  /** Base filters locked by the page (e.g. { cityId: 42 }) */
  baseFilters?: Partial<SearchEventsParams>;
  /** Hide the auto-resolved CITY chip while still surfacing every
   *  other chip (dates / price / artists / venues) as the user sets
   *  them. The concerts home page passes `true` — its city is
   *  resolved silently by a 3-tier fallback (localStorage → geo →
   *  London) and should never appear as a removable chip (clearing
   *  it would empty the listing with no way to get it back). The
   *  chips row itself stays hidden until the user sets at least one
   *  OTHER filter, because `chips.length > 0` gates the whole row. */
  hideCityChip?: boolean;
}

type DropdownKey =
  | 'dateRange'
  | 'cityId'
  | 'participantId'
  | 'venueId'
  | 'teamType'
  | 'price'
  | 'sortBy'
  | null;

// Which filters each page type should show. `venueId` is present
// everywhere the listing isn't already scoped to a single venue by
// the route itself (the `venue` pageType — `/concerts/venue/[slug]`
// — locks via `baseFilters` and drops the redundant filter).
const VISIBLE_FILTERS: Record<ListingPageType, string[]> = {
  search: ['dateRange', 'cityId', 'participantId', 'venueId', 'price'],
  team: ['dateRange', 'cityId', 'venueId', 'price'],
  venue: ['dateRange', 'cityId', 'participantId', 'price'],
  city: ['dateRange', 'participantId', 'venueId', 'price'],
};

/** Format date as "Thu, 26 Mar" */
function fmtShort(d: Date): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}

/** DD/MM/YYYY format */
function ddmmyyyy(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function buildDateOptions(): { label: string; dateFrom: string; dateTo: string }[] {
  const now = new Date();
  const today = ddmmyyyy(now);

  // Next 7 days: today + 6 more days (includes today)
  const in6 = new Date(now);
  in6.setDate(now.getDate() + 6);
  const next7end = ddmmyyyy(in6);

  // This month: today to last day of current month
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const monthEnd = ddmmyyyy(lastDay);

  return [
    { label: 'All Dates', dateFrom: '', dateTo: '' },
    { label: 'Today', dateFrom: today, dateTo: today },
    { label: 'Next 7 Days', dateFrom: today, dateTo: next7end },
    { label: 'This Month', dateFrom: today, dateTo: monthEnd },
  ];
}

const DATE_OPTIONS = buildDateOptions();

const SORT_OPTIONS = [
  { value: '', label: 'Our Recommendations' },
  { value: 'minTicketPrice.price:asc', label: 'Price: Low to High' },
  { value: 'minTicketPrice.price:desc', label: 'Price: High to Low' },
];

const TEAM_TYPE_OPTIONS = [
  { value: 'home', label: 'Home Team' },
  { value: 'away', label: 'Away Team' },
  { value: 'homeAway', label: 'Home/Away Team' },
];

/**
 * Standalone memoized price slider — prevents parent re-renders from causing blink.
 * Uses internal refs for slider values so dragging doesn't trigger React re-renders.
 */
/**
 * Price slider — fully self-contained. Does NOT call parent setState during drag.
 * Only communicates values to parent via onApply callback.
 */
const PriceSlider = memo(function PriceSlider({
  rangeMin,
  rangeMax,
  initialMin,
  initialMax,
  currencySymbol,
  onApply,
  onClear,
}: {
  rangeMin: number;
  rangeMax: number;
  initialMin: number;
  initialMax: number;
  currencySymbol: string;
  onApply: (min: string, max: string) => void;
  onClear: () => void;
}) {
  const [localMin, setLocalMin] = useState(initialMin);
  const [localMax, setLocalMax] = useState(initialMax);

  // filterOptions loads asynchronously after mount. When the real price range
  // arrives, initialMin/initialMax props change — sync local state so the
  // slider reflects the actual data range rather than the 10000 fallback.
  useEffect(() => {
    setLocalMin(initialMin);
    setLocalMax(initialMax);
  }, [initialMin, initialMax]);

  const pct = (v: number) =>
    rangeMax > rangeMin ? ((v - rangeMin) / (rangeMax - rangeMin)) * 100 : 0;

  return (
    <div className="double-slider-box">
      <div className="price-range-text">
        <div className="d-flex align-items-center gap-2">
          <div className="min-box form-control">
            <span className="price-currency">{currencySymbol}</span>
            <span className="min-input">{Math.round(localMin)}</span>
          </div>
          <span>-</span>
          <div className="max-box form-control">
            <span className="price-currency">{currencySymbol}</span>
            <span className="max-input">{Math.round(localMax)}</span>
          </div>
        </div>
      </div>
      <div className="range-slider">
        <span
          className="slider-track"
          style={{
            background: `linear-gradient(to right, #e6dff0 ${pct(localMin)}%, var(--primary-hover, #a855c9) ${pct(localMin)}%, var(--primary-hover, #a855c9) ${pct(localMax)}%, #e6dff0 ${pct(localMax)}%)`,
          }}
        />
        <input
          type="range"
          className="min-val"
          min={rangeMin}
          max={rangeMax}
          value={localMin}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (v <= localMax) setLocalMin(v);
          }}
        />
        <input
          type="range"
          className="max-val"
          min={rangeMin}
          max={rangeMax}
          value={localMax}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (v >= localMin) setLocalMax(v);
          }}
        />
      </div>
      <div className="pt-3 d-flex justify-content-end price-footer">
        <button
          className="btn btn-secondary btn-sm mr-2"
          onClick={() => {
            setLocalMin(rangeMin);
            setLocalMax(rangeMax);
            onClear();
          }}
        >
          Clear
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => onApply(String(localMin), String(localMax))}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// ─── Module-level helpers ────────────────────────────────────────────────────
// These must NOT be defined inside EventsFilters. Defining component functions
// (or utilities they rely on) inside a parent component creates a new function
// reference on every render. React uses the component *type* for reconciliation:
// a new reference = a different type = full unmount + remount on every re-render.
// For a scrollable list this means:
//   • scroll position resets to top every time a checkbox is checked (Issue 1)
//   • click handlers become unreliable between the unmount/remount (Issue 2)

function toggleInSet(set: Set<string>, val: string): Set<string> {
  const next = new Set(set);
  if (next.has(val)) next.delete(val);
  else next.add(val);
  return next;
}

function MultiSelectPanel({
  items,
  pending,
  onToggle,
  onClear,
  onApply,
}: {
  items: FilterOptionItem[];
  pending: Set<string>;
  onToggle: (id: string) => void;
  onClear: () => void;
  onApply: () => void;
}) {
  return (
    <>
      {items.length === 0 ? (
        <p className="text-muted small">No options available</p>
      ) : (
        <ul>
          {items.map((item) => (
            <li key={item.id}>
              <label className="filter-checkbox-item">
                <input
                  type="checkbox"
                  checked={pending.has(String(item.id))}
                  onChange={() => onToggle(String(item.id))}
                />
                <span className="item-label">{item.name}</span>
                {item.count !== undefined && <span className="item-count">({item.count})</span>}
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="filter-dropdown-footer">
        <button className="btn btn-secondary btn-sm" onClick={onClear}>
          Clear
        </button>
        <button className="btn btn-primary btn-sm" onClick={onApply}>
          Apply
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function EventsFilters({
  basePath = '/concerts',
  title: _title = 'Sports',
  total,
  pageType = 'search',
  baseFilters = {},
  hideCityChip = false,
}: EventsFiltersProps) {
  const nextSearchParams = useSearchParams();
  const { currencySymbol, currencyCode, exchangeRates, convertPrice } = useCurrency();

  // SE365 syncs all event prices in GBP (the sync service requests currency=GBP).
  // We convert the price range for display (GBP → user currency) and convert back
  // when applying the filter (user currency → GBP) so the backend receives values
  // it can compare against the raw GBP prices stored in the database.
  const gbpToUser = (gbp: number): number => convertPrice(gbp, 'GBP');

  // Reverse: user-currency amount → GBP for URL params sent to backend.
  // exchangeRates['GBP'] = how many GBP equal 1 unit of user currency.
  // When userCurrency IS GBP there is no self-rate → falls back to 1 (no-op).
  const userToGbp = (user: number): number =>
    currencyCode === 'GBP' ? user : Math.round(user * (exchangeRates['GBP'] || 1) * 100) / 100;
  const [openDropdown, setOpenDropdown] = useState<DropdownKey>(null);
  const openDropdownRef = useRef<DropdownKey>(null);
  openDropdownRef.current = openDropdown;
  const containerRef = useRef<HTMLDivElement>(null);

  // Track the current filtered result count. Seeded from the server-rendered
  // total (prop) and kept in sync with EventsList via the `sportsTotalChanged`
  // custom event. When 0, every filter/sort button is rendered as disabled so
  // the user can't narrow results on an already-empty set.
  const [currentTotal, setCurrentTotal] = useState<number>(total ?? Number.NaN);
  useEffect(() => {
    if (typeof total === 'number') setCurrentTotal(total);
  }, [total]);
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ total: number }>;
      if (typeof ce.detail?.total === 'number') {
        setCurrentTotal(ce.detail.total);
      }
    };
    window.addEventListener('sportsTotalChanged', handler);
    return () => window.removeEventListener('sportsTotalChanged', handler);
  }, []);
  // Only disable once we've actually heard a total; NaN = unknown (initial
  // render before EventsList has emitted) so we leave filters usable.
  const filtersDisabled = currentTotal === 0;
  // If the list becomes empty while a dropdown is open, close it.
  useEffect(() => {
    if (filtersDisabled) setOpenDropdown(null);
  }, [filtersDisabled]);

  // Sort is intentionally NOT persisted in the URL — it resets to the default
  // on every page load. EventsList listens for `sportsSortChanged` events.
  const [sortState, setSortState] = useState<{ sortBy: string; sortOrder: string }>({
    sortBy: '',
    sortOrder: '',
  });

  // Local params — updated immediately on every filter change so chips/labels react instantly
  const [localParams, setLocalParams] = useState(
    () => new URLSearchParams(nextSearchParams.toString()),
  );
  useEffect(() => {
    setLocalParams(new URLSearchParams(nextSearchParams.toString()));
  }, [nextSearchParams]);

  // All reads go through this — always up to date
  const searchParams = useMemo(
    () => ({
      get: (key: string) => localParams.get(key),
      toString: () => localParams.toString(),
    }),
    [localParams],
  );

  /** Update URL without Next.js navigation — no component unmount, no blink */
  const navigateFilter = useCallback((url: string) => {
    // Decode %2F back to / so dates show as DD/MM/YYYY in the URL bar
    const decoded = decodeURIComponent(url);
    const u = new URL(decoded, window.location.origin);
    setLocalParams(new URLSearchParams(u.search));
    window.history.replaceState(window.history.state, '', decoded);
    window.dispatchEvent(new CustomEvent('filtersChanged'));
  }, []);

  // Dynamic filter options from API
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  // Parse DD/MM/YYYY string to Date
  function parseDateParam(str: string | null): Date | null {
    if (!str) return null;
    const parts = str.split('/');
    if (parts.length === 3) return new Date(+parts[2], +parts[1] - 1, +parts[0]);
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }

  // Custom date range state — initialize from URL if present
  const hasDateInUrl = !!(searchParams.get('dateFrom') && searchParams.get('dateTo'));
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customFrom, setCustomFrom] = useState<Date | null>(() =>
    parseDateParam(searchParams.get('dateFrom')),
  );
  const [customTo, setCustomTo] = useState<Date | null>(() =>
    parseDateParam(searchParams.get('dateTo')),
  );

  // Multi-select pending states (not applied until "Apply" clicked)
  const [pendingTeams, setPendingTeams] = useState<Set<string>>(new Set());
  const [pendingTeamTypes, setPendingTeamTypes] = useState<Set<string>>(new Set());
  // Venue is a multi-select checkbox filter (concerts backend DTO
  // now accepts CSV via `parseIdList`, matching participantId).
  // Buffered as pending until "Apply" is clicked, same UX as the
  // other multi-selects.
  const [pendingVenues, setPendingVenues] = useState<Set<string>>(new Set());
  const [pendingMinPrice, setPendingMinPrice] = useState('');
  const [pendingMaxPrice, setPendingMaxPrice] = useState('');

  const visibleFilters = VISIBLE_FILTERS[pageType];

  const client = useMemo(() => new SportsEventsClient(CONCERTS_API_BASE_URL), []);

  // Re-fetch filter options whenever non-price URL params change so the price
  // range reflects the current filtered result set. minPrice/maxPrice are
  // intentionally excluded to avoid a circular dependency.
  //
  // IMPORTANT: URL params are merged ON TOP of baseFilters — not with blanket
  // spread-then-override. Spreading `...baseFilters` and then writing
  // `eventTypeId: undefined` (when the param is absent from the URL) would
  // silently overwrite the page-level lock (e.g. { eventTypeId: 5 } on a
  // Football category page), causing getFilterOptions to return the global
  // price range instead of the category-specific one.
  // Filter-options query: deliberately scoped to the *page context* only
  // (base filters from the route + search keyword + date range). EVERY
  // user-applied filter — including their cityId / venueId selection from
  // the dropdowns — is intentionally excluded so every dropdown keeps
  // showing the full catalogue of options for the page. Otherwise picking
  // a city via the filter would collapse the cities dropdown itself to
  // just that one city (the bug the user reported), and ticking "Football"
  // would shrink Teams / Tournaments / Cities to the Football subset,
  // leaving no way to broaden without clearing first.
  //
  // baseFilters.cityId / baseFilters.venueId are still honoured because
  // those are *route-locked* (e.g. on /concerts/city/barcelona-1005) — the
  // user can't change them without navigating away.
  const filterOptionsQueryKey = useMemo(() => {
    const sp = localParams;
    const query: Record<string, unknown> = { ...baseFilters };
    const search = sp.get('search');
    if (search) query.search = search;
    const dateFrom = sp.get('dateFrom');
    if (dateFrom) query.dateFrom = dateFrom;
    const dateTo = sp.get('dateTo');
    if (dateTo) query.dateTo = dateTo;
    // `eventId` deep-links the listing to a single event. When present
    // it MUST narrow the filter-option counts too — otherwise the
    // dropdowns advertise "London (250)" while the listing is showing
    // exactly one event. Treated like `cityId`/`venueId` (route-locked)
    // rather than a user-applied filter the dropdown should ignore.
    const eventId = sp.get('eventId');
    if (eventId) {
      const n = parseInt(eventId, 10);
      if (Number.isFinite(n)) query.eventId = n;
    }
    return JSON.stringify(query);
  }, [localParams, baseFilters]);

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(() => {
      const query = JSON.parse(filterOptionsQueryKey) as Partial<SearchEventsParams>;
      client
        .getFilterOptions(query as SearchEventsParams)
        .then((opts) => {
          if (!cancelled) setFilterOptions(opts);
        })
        .catch(() => {});
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [filterOptionsQueryKey, client]);

  // Concerts-only: derive the venue dropdown client-side. The
  // concerts backend's `/filter-options` response doesn't include a
  // `venues` aggregate (shared code path with sports; can't extend
  // it without touching sports), so we run a broad `searchEvents`
  // scoped by the same page context (baseFilters + date/search/
  // eventId) at limit 500, then extract UNIQUE `{ id, name }` off
  // the events' `venue` field. Falls back to the sibling `venues`
  // field on `filterOptions` when a backend rebuild eventually
  // ships it — the merge is dedup-safe. Runs only when the venue
  // dropdown is visible.
  const [derivedVenues, setDerivedVenues] = useState<
    Array<{ id: number; name: string; count: number }>
  >([]);
  // LAZY: fire the `limit=500` venue-derivation search ONLY when the
  // Venue dropdown is actually opened by the user (or is currently
  // open and the page-context query changes). Firing it eagerly on
  // mount hit `/events?...&limit=500` on every listing page load,
  // even when the user never opened the dropdown — a 500-row fetch
  // for zero UI. Cache the last-fired query key so re-opening the
  // dropdown without a context change is a no-op.
  const lastVenueFetchKeyRef = useRef<string>('');
  useEffect(() => {
    if (!visibleFilters.includes('venueId')) return undefined;
    if (openDropdown !== 'venueId') return undefined;
    if (lastVenueFetchKeyRef.current === filterOptionsQueryKey) return undefined;
    let cancelled = false;
    const timeout = setTimeout(() => {
      lastVenueFetchKeyRef.current = filterOptionsQueryKey;
      const query = JSON.parse(filterOptionsQueryKey) as Partial<SearchEventsParams>;
      client
        .searchEvents({ ...(query as SearchEventsParams), page: 1, limit: 500 })
        .then((result) => {
          if (cancelled) return;
          const counts = new Map<number, { name: string; count: number }>();
          for (const ev of result?.data ?? []) {
            const id = (ev as any)?.venue?.id;
            const name = (ev as any)?.venue?.name;
            if (typeof id !== 'number' || !Number.isFinite(id) || !name) continue;
            const existing = counts.get(id);
            if (existing) {
              existing.count += 1;
            } else {
              counts.set(id, { name: String(name), count: 1 });
            }
          }
          const venues = Array.from(counts.entries())
            .map(([id, v]) => ({ id, name: v.name, count: v.count }))
            .sort((a, b) => {
              if (b.count !== a.count) return b.count - a.count;
              return a.name.localeCompare(b.name);
            });
          setDerivedVenues(venues);
        })
        .catch(() => {
          if (!cancelled) setDerivedVenues([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDropdown, filterOptionsQueryKey, client, pageType]);

  // Effective venues list: prefer a backend-emitted `venues` array
  // if a future concerts backend ships it; else use the client-side
  // derivation above.
  const effectiveVenues =
    ((filterOptions as any)?.venues as
      | Array<{ id: number; name: string; count?: number }>
      | undefined) && (filterOptions as any).venues.length > 0
      ? ((filterOptions as any).venues as Array<{ id: number; name: string; count?: number }>)
      : derivedVenues;

  // Narrowed query — page context PLUS every user-applied filter EXCEPT
  // `cityId` / `venueId`. We reuse this query to compute the disable set
  // for the cities dropdown: "which cities would still have events if the
  // user kept all their *other* filters but switched to this city?".
  // Including the user's own cityId here would collapse the result to
  // just that one city (everything else "doesn't match"), which is the
  // opposite of useful for a disable check. Same reasoning for venueId.
  const narrowFilterOptionsQueryKey = useMemo(() => {
    const sp = localParams;
    const query: Record<string, unknown> = { ...baseFilters };
    const search = sp.get('search');
    if (search) query.search = search;
    const participantId = sp.get('participantId');
    if (participantId) query.participantId = participantId;
    const teamType = sp.get('teamType');
    if (teamType) query.teamType = teamType;
    const minPrice = sp.get('minPrice');
    if (minPrice) query.minPrice = Number(minPrice);
    const maxPrice = sp.get('maxPrice');
    if (maxPrice) query.maxPrice = Number(maxPrice);
    const dateFrom = sp.get('dateFrom');
    if (dateFrom) query.dateFrom = dateFrom;
    const dateTo = sp.get('dateTo');
    if (dateTo) query.dateTo = dateTo;
    // Same reasoning as `filterOptionsQueryKey` — `eventId` pins the
    // listing to a single event, so the disable set has to narrow to
    // that event too. Otherwise cities/venues outside that single
    // event would still render enabled.
    const eventId = sp.get('eventId');
    if (eventId) {
      const n = parseInt(eventId, 10);
      if (Number.isFinite(n)) query.eventId = n;
    }
    return JSON.stringify(query);
  }, [localParams, baseFilters]);

  const [narrowFilterOptions, setNarrowFilterOptions] = useState<FilterOptions | null>(null);
  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(() => {
      const query = JSON.parse(narrowFilterOptionsQueryKey) as Partial<SearchEventsParams>;
      client
        .getFilterOptions(query as SearchEventsParams)
        .then((opts) => {
          if (!cancelled) setNarrowFilterOptions(opts);
        })
        .catch(() => {});
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [narrowFilterOptionsQueryKey, client]);

  // Set of city ids that match the current selection — anything outside this
  // set is rendered disabled in the city dropdown. Empty set during the
  // initial fetch means "no info yet" and we leave everything enabled.
  const matchingCityIds = useMemo<Set<string> | null>(() => {
    if (!narrowFilterOptions) return null;
    return new Set(narrowFilterOptions.cities.map((c) => String(c.id)));
  }, [narrowFilterOptions]);

  // Sync pending multi-selects from URL on mount / URL change
  useEffect(() => {
    const sp = searchParams;
    setPendingTeams(new Set(sp.get('participantId')?.split(',').filter(Boolean) ?? []));
    setPendingTeamTypes(new Set(sp.get('teamType')?.split(',').filter(Boolean) ?? []));
    setPendingVenues(new Set(sp.get('venueId')?.split(',').filter(Boolean) ?? []));
    setPendingMinPrice(sp.get('minPrice') || '');
    setPendingMaxPrice(sp.get('maxPrice') || '');
    // Sync custom date range from URL
    setCustomFrom(parseDateParam(sp.get('dateFrom')));
    setCustomTo(parseDateParam(sp.get('dateTo')));
  }, [searchParams]);

  // ──────── URL Helpers ────────

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set('page', '1');
    navigateFilter(`${basePath}?${params.toString()}`);
    setOpenDropdown(null);
  };

  const updateMultiFilter = (key: string, values: Set<string>) => {
    const params = new URLSearchParams(searchParams.toString());
    const joined = Array.from(values).join(',');
    if (joined) {
      params.set(key, joined);
    } else {
      params.delete(key);
    }
    params.set('page', '1');
    navigateFilter(`${basePath}?${params.toString()}`);
    setOpenDropdown(null);
  };

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams.toString());
    // Remove all filter params but keep non-filter params — including
    // `search` (spec 2026-08-10 revised: reset should clear the
    // date / city / venue / participant / price selections but LEAVE
    // the search-driven concert-name scoping in place so the user
    // doesn't lose their event context on a filter reset). Removing
    // the search-bar chip is a separate action (its own clear-x
    // button next to the input, plus `clearFilters` on non-search
    // listing pages where `search` was never in the URL to start).
    // sortBy/sortOrder intentionally omitted — sort lives in local state.
    const filterKeys = [
      'dateRange',
      'datePreset',
      'dateFrom',
      'dateTo',
      'cityId',
      'participantId',
      'venueId',
      'teamType',
      'minPrice',
      'maxPrice',
    ];
    filterKeys.forEach((k) => params.delete(k));
    params.set('page', '1');
    const qs = params.toString();
    navigateFilter(qs ? `${basePath}?${qs}` : basePath);
    setOpenDropdown(null);
    setShowCustomPicker(false);
    setCustomFrom(null);
    setCustomTo(null);
    setPendingMinPrice('');
    setPendingMaxPrice('');
    setPendingTeams(new Set());
    setPendingTeamTypes(new Set());
    setPendingVenues(new Set());
    // Reset sort too (separate from URL-backed filters).
    setSortState({ sortBy: '', sortOrder: '' });
    window.dispatchEvent(
      new CustomEvent('sportsSortChanged', { detail: { sortBy: '', sortOrder: '' } }),
    );
  };

  const removeChip = (key: string, value?: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value && params.get(key)?.includes(',')) {
      // Remove single value from comma-separated
      const vals = params
        .get(key)!
        .split(',')
        .filter((v) => v !== value);
      if (vals.length) params.set(key, vals.join(','));
      else params.delete(key);
    } else {
      params.delete(key);
      if (key === 'minPrice') params.delete('maxPrice');
      if (key === 'maxPrice') params.delete('minPrice');
      if (key === 'dateFrom') {
        params.delete('dateTo');
        params.delete('datePreset');
      }
      if (key === 'dateTo') params.delete('dateFrom');
    }
    params.set('page', '1');
    navigateFilter(`${basePath}?${params.toString()}`);
  };

  const toggleDropdown = (key: DropdownKey) => {
    // Reset custom date picker when closing date dropdown or switching away from it
    if (openDropdown === 'dateRange') {
      setShowCustomPicker(false);
      setCustomFrom(parseDateParam(searchParams.get('dateFrom')));
      setCustomTo(parseDateParam(searchParams.get('dateTo')));
    }
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  // Auto-detect if a dropdown should open upward when there isn't enough
  // space below. Re-runs when `showCustomPicker` toggles too — the date
  // dropdown grows much taller once the inline calendar opens, so we need
  // to re-measure and may need to flip it above the button.
  useEffect(() => {
    if (!openDropdown) return;

    // Re-measure each open dropdown panel and flip it `open-up` if it would
    // overflow the viewport bottom. Pulled out so we can call it on the
    // initial render AND from a `resize` listener — the previous version
    // measured once on `openDropdown` change and never again, so opening
    // DevTools (which shrinks the viewport without changing React state)
    // left dropdowns positioned for the old viewport height. They'd then
    // appear half off-screen until the user closed and re-opened them.
    const reMeasure = () => {
      const panels = containerRef.current?.querySelectorAll('.filter-dropdown-panel');
      if (!panels) return;
      panels.forEach((panel) => {
        const el = panel as HTMLElement;
        el.classList.remove('open-up');
        const rect = el.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
          el.classList.add('open-up');
        }
      });
    };

    // Wait for React render + DOM paint before the first measurement so
    // getBoundingClientRect sees the actual rendered panel size.
    const timer = setTimeout(reMeasure, 10);
    window.addEventListener('resize', reMeasure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', reMeasure);
    };
  }, [openDropdown, showCustomPicker]);

  /**
   * Mobile bottom-sheet injector.
   *
   * Pure CSS attempts to pin the dropdown to the viewport bottom on
   * mobile kept losing to either a deeply-nested parent's
   * positioning context or a !important rule the project picked up
   * along the way. Inline `style` attributes have higher
   * precedence than every CSS rule (including `!important`-marked
   * ones at the same specificity tier — inline `!important` wins
   * outright), so we sidestep the whole specificity fight by
   * walking the open panel's DOM node and stamping the bottom-
   * sheet styles directly onto it.
   *
   * Runs in `useLayoutEffect` so the styles land before paint —
   * no flicker between the desktop-anchored render and the
   * mobile-pinned final state. Cleaning up isn't needed: when
   * `openDropdown` flips to null, React unmounts the panel and
   * the inline styles vanish with it.
   *
   * Desktop (`> 767px`): the effect short-circuits and the
   * existing `position: absolute; top: 100%` styles render as-is.
   */
  useLayoutEffect(() => {
    if (!openDropdown) return;
    if (typeof window === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return;

    // Apply via a microtask delay so the panel exists in the DOM
    // by the time we go looking for it (the conditional render
    // `{openDropdown === 'X' && <div ...>}` mounts on the same
    // tick as this effect, but `querySelector` runs against the
    // committed DOM — which `useLayoutEffect` does see post-
    // commit, but for safety we also handle a future race where
    // the panel mounts inside a nested Suspense boundary).
    const apply = () => {
      const panels = containerRef.current?.querySelectorAll<HTMLElement>('.filter-dropdown-panel');
      if (!panels) return;
      panels.forEach((el) => {
        // Reset the `.open-up` flip — desktop-only logic that
        // would otherwise push the bottom-sheet off the top.
        el.classList.remove('open-up');
        const s = el.style;
        s.setProperty('position', 'fixed', 'important');
        s.setProperty('top', 'auto', 'important');
        s.setProperty('bottom', '0', 'important');
        s.setProperty('left', '0', 'important');
        s.setProperty('right', '0', 'important');
        s.setProperty('width', '100vw', 'important');
        s.setProperty('max-width', '100vw', 'important');
        s.setProperty('min-width', '0', 'important');
        s.setProperty('margin', '0', 'important');
        s.setProperty('border', 'none', 'important');
        s.setProperty('border-radius', '16px 16px 0 0', 'important');
        s.setProperty('box-shadow', '0 -8px 24px rgba(0,0,0,0.2)', 'important');
        s.setProperty('z-index', '1060', 'important');
        s.setProperty('max-height', '80vh', 'important');
        s.setProperty('overflow-y', 'auto', 'important');
        s.setProperty(
          'padding',
          '20px 16px calc(20px + env(safe-area-inset-bottom, 0px))',
          'important',
        );
      });

      // Backdrop — same idea. The component already renders the
      // `<div className="filter-mobile-overlay">` when
      // `openDropdown` is non-null; we just need to guarantee
      // its layout overrides win on mobile.
      const overlay = containerRef.current?.querySelector<HTMLElement>('.filter-mobile-overlay');
      if (overlay) {
        const s = overlay.style;
        s.setProperty('display', 'block', 'important');
        s.setProperty('position', 'fixed', 'important');
        s.setProperty('top', '0', 'important');
        s.setProperty('left', '0', 'important');
        s.setProperty('right', '0', 'important');
        s.setProperty('bottom', '0', 'important');
        s.setProperty('background', 'rgba(0,0,0,0.45)', 'important');
        s.setProperty('z-index', '1055', 'important');
      }
    };
    apply();
    // Re-run on viewport resize so opening DevTools / rotating
    // the device updates the pinned position.
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [openDropdown, showCustomPicker]);

  // Close dropdown on outside click (but not when clicking inside the open dropdown or datepicker)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking inside a datepicker calendar
      if (
        target?.closest?.(
          '.react-datepicker, .react-datepicker__month-container, .react-datepicker-popper',
        )
      )
        return;
      // Don't close if clicking inside the currently open dropdown panel
      if (target?.closest?.('.filter-dropdown-panel')) return;
      // Only reset custom date picker state when actually closing the date dropdown
      if (openDropdownRef.current === 'dateRange') {
        setShowCustomPicker(false);
        setCustomFrom(parseDateParam(searchParams.get('dateFrom')));
        setCustomTo(parseDateParam(searchParams.get('dateTo')));
      }
      if (openDropdownRef.current !== null) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchParams]);

  // ──────── Sort Helpers ────────
  // Sort is local-only (not in URL) so it resets on refresh.

  const sortLabel = () => {
    const { sortBy, sortOrder } = sortState;
    if (sortBy === 'minTicketPrice.price' && sortOrder === 'desc') return 'Price: High to Low';
    if (sortBy === 'minTicketPrice.price') return 'Price: Low to High';
    return 'Our Recommendations';
  };

  const applySortOption = (combined: string) => {
    let next = { sortBy: '', sortOrder: '' };
    if (combined) {
      const [field, order] = combined.split(':');
      next = { sortBy: field, sortOrder: order };
    }
    setSortState(next);
    window.dispatchEvent(new CustomEvent('sportsSortChanged', { detail: next }));
    setOpenDropdown(null);
  };

  // ──────── Active Chips ────────

  const chips: { key: string; label: string; value?: string }[] = [];

  // Note: the search keyword is intentionally NOT shown as a chip — it's
  // already surfaced in the SearchBar input and the H4 results heading
  // ("All events - 10 sports found for 'Madrid'"), so a third copy as a
  // chip is just visual noise.

  const df = searchParams.get('dateFrom');
  const dt = searchParams.get('dateTo');
  if (df && dt) {
    const presetLabel = searchParams.get('datePreset');
    if (presetLabel) {
      chips.push({ key: 'dateFrom', label: presetLabel });
    } else {
      const fromD = parseDateParam(df);
      const toD = parseDateParam(dt);
      const chipLabel = fromD && toD ? `${fmtShort(fromD)} - ${fmtShort(toD)}` : `${df} – ${dt}`;
      chips.push({ key: 'dateFrom', label: chipLabel });
    }
  }

  // City chip (only if cityId is a URL param, not a base filter).
  // Home page suppresses this chip entirely (`hideCityChip`) — its
  // city is auto-resolved by the 3-tier fallback and shouldn't be
  // presented as user-removable (clearing it empties the listing).
  const cityParam = searchParams.get('cityId');
  if (cityParam && !baseFilters.cityId && !hideCityChip) {
    const cityName =
      filterOptions?.cities.find((c) => String(c.id) === cityParam)?.name || `City ${cityParam}`;
    chips.push({ key: 'cityId', label: cityName });
  }

  // Multi-select chips: artists (backend field is still `participantId`
  // / `teams[]` — only the customer-visible label reads as "Artist"
  // on the concerts domain).
  const teamsParam = searchParams.get('participantId');
  if (teamsParam && !baseFilters.participantId) {
    teamsParam.split(',').forEach((v) => {
      const name = filterOptions?.teams.find((t) => String(t.id) === v)?.name || `Artist ${v}`;
      chips.push({ key: 'participantId', label: name, value: v });
    });
  }

  // Multi-select chips: venues — only when the current page actually
  // surfaces the Venue dropdown. `visibleFilters` excludes the
  // `venue` pageType (the `/concerts/venue/[slug]` listing is
  // already scoped to a single venue via `baseFilters.venueId`), so
  // a legacy `?venueId=` URL on that page silently drops the chip
  // rather than offering a redundant filter.
  const venuesParam = searchParams.get('venueId');
  if (venuesParam && !baseFilters.venueId && visibleFilters.includes('venueId')) {
    venuesParam.split(',').forEach((v) => {
      const name =
        // `effectiveVenues` = backend-emitted `venues` array when
        // present, else the client-side derivation from a broad
        // event scan (see `derivedVenues` above). Either way, look
        // up the display name by id.
        effectiveVenues.find((venue) => String(venue.id) === v)?.name || `Venue ${v}`;
      chips.push({ key: 'venueId', label: name, value: v });
    });
  }

  // Team type chips — only when the current page actually surfaces
  // the Team Type dropdown. Gate matches the eventTypeId chip guard
  // above so legacy `?teamType=` URLs (bookmarks / external
  // referrals) don't render an orphaned chip on concerts pages
  // where the dropdown is gone.
  const teamTypeParam = searchParams.get('teamType');
  if (teamTypeParam && visibleFilters.includes('teamType')) {
    teamTypeParam.split(',').forEach((v) => {
      const label = TEAM_TYPE_OPTIONS.find((o) => o.value === v)?.label || v;
      chips.push({ key: 'teamType', label, value: v });
    });
  }

  // Price chip — convert stored EUR values to user currency for the chip label.
  const minP = searchParams.get('minPrice');
  const maxP = searchParams.get('maxPrice');
  if (minP || maxP) {
    const chipMin = minP ? Math.round(gbpToUser(Number(minP))) : 0;
    const chipMax = maxP ? Math.round(gbpToUser(Number(maxP))) : '∞';
    chips.push({
      key: 'minPrice',
      label: `${currencySymbol} ${chipMin} – ${currencySymbol} ${chipMax}`,
    });
  }

  // Sort — not shown in chips (visible in sort button label)

  // ──────── Render Helpers ────────

  const FilterButton = ({ dropKey, label }: { dropKey: DropdownKey; label: string }) => {
    // The price slider writes `minPrice`/`maxPrice` instead of a single
    // `price` URL param, and the date filter writes `dateFrom`/`dateTo`
    // (or `datePreset`) instead of `dateRange`, so each needs a custom
    // probe to drive the active styling.
    const active =
      !!dropKey &&
      (dropKey === 'price'
        ? !!(searchParams.get('minPrice') || searchParams.get('maxPrice'))
        : dropKey === 'dateRange'
          ? hasDateInUrl
          : !!searchParams.get(dropKey as string));
    return (
      <button
        type="button"
        onClick={() => toggleDropdown(dropKey)}
        disabled={filtersDisabled}
        className={`filter-btn ${active ? 'active' : ''} ${openDropdown === dropKey ? 'open' : ''} ${filtersDisabled ? 'disabled' : ''}`}
      >
        {label}
        <i className="fa-regular fa-chevron-down"></i>
      </button>
    );
  };

  // Single-select option list
  const SingleOptionList = ({
    filterKey,
    options,
    enabledValues,
  }: {
    filterKey: string;
    options: { value: string; label: string }[];
    /** When supplied, options whose `value` is NOT in this set render as
     *  disabled (visible but unclickable). `undefined` keeps every option
     *  enabled (default). */
    enabledValues?: Set<string> | null;
  }) => {
    const current = searchParams.get(filterKey) || '';
    return (
      <ul>
        {options.map((opt) => {
          // Always allow toggling the *currently selected* value off, even
          // if the narrow set says it doesn't match — otherwise the user
          // could get stuck with an applied filter they can't deselect.
          const isSelected = current === opt.value;
          const isDisabled = !!enabledValues && !enabledValues.has(opt.value) && !isSelected;
          return (
            <li key={opt.value}>
              <button
                className={`filter-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                disabled={isDisabled}
                aria-disabled={isDisabled || undefined}
                onClick={() =>
                  isDisabled ? undefined : updateFilter(filterKey, isSelected ? '' : opt.value)
                }
              >
                {opt.label}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  // Date label for button — always returns the static label per design,
  // matching the City and Price filter buttons. The active selection is
  // communicated via the chip row + the dropdown's preset-list highlight,
  // not by mutating the button text.
  const activeDateLabel = () => 'Date';

  // City label for button — always returns the static label per design:
  // the active selection is communicated via the chip row + dropdown's
  // selected-state styling, not by mutating the button text.
  const activeCityLabel = () => 'City';

  // Count of multi-select for button label
  const multiLabel = (key: string, defaultLabel: string) => {
    const val = searchParams.get(key);
    if (!val) return defaultLabel;
    const count = val.split(',').length;
    return count === 1 ? `${defaultLabel} (1)` : `${defaultLabel} (${count})`;
  };

  // Price label — always returns the static label per design. The active
  // range is communicated via the chip row + the slider's persisted
  // pending values, not by mutating the button text.
  const priceLabel = () => 'Price';

  return (
    <section className="filters-section pb-4 pt-4" ref={containerRef}>
      {/* Mobile-only backdrop. Tapping it closes the open dropdown the
          same way an outside-document click does on desktop (the
          existing `mousedown` listener on `document` picks this up
          because the overlay is outside `.filter-dropdown-panel`).
          The overlay is gated by CSS to be visible only at
          ≤767px wide, so desktop users never see it. */}
      {openDropdown && (
        <div
          className="filter-mobile-overlay d-md-none"
          aria-hidden="true"
          onClick={() => setOpenDropdown(null)}
        />
      )}
      <div className="container">
        <div className="row">
          <div className="col">
            <div className="row pt-3 align-items-center">
              {/* Filter buttons */}
              <div className="col-lg-9 col-md-12 mb-lg-0 mb-2 d-flex flex-wrap align-items-center">
                {/* Date Filter */}
                {visibleFilters.includes('dateRange') && (
                  <div className="position-relative">
                    <FilterButton dropKey="dateRange" label={activeDateLabel()} />
                    {openDropdown === 'dateRange' && (
                      <div
                        className={`filter-dropdown-panel cd-panel ${showCustomPicker ? 'wide' : ''}`}
                      >
                        <ul>
                          {DATE_OPTIONS.map((opt) => {
                            const currentPreset = searchParams.get('datePreset') || '';
                            const currentFrom = searchParams.get('dateFrom') || '';
                            const isSelected = opt.dateFrom ? currentPreset === opt.label : false;
                            const isClear = !opt.dateFrom && !currentFrom;
                            return (
                              <li key={opt.label}>
                                <button
                                  className={`filter-option ${isSelected || (isClear && opt.label === 'All Dates') ? 'selected' : ''}`}
                                  onClick={() => {
                                    const params = new URLSearchParams(searchParams.toString());
                                    params.delete('dateRange');
                                    if (opt.dateFrom && opt.dateTo) {
                                      params.set('dateFrom', opt.dateFrom);
                                      params.set('dateTo', opt.dateTo);
                                      params.set('datePreset', opt.label);
                                    } else {
                                      params.delete('dateFrom');
                                      params.delete('dateTo');
                                      params.delete('datePreset');
                                    }
                                    params.set('page', '1');
                                    navigateFilter(`${basePath}?${params.toString()}`);
                                    setOpenDropdown(null);
                                    setShowCustomPicker(false);
                                  }}
                                >
                                  {opt.label}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                        {/* Custom Date Range */}
                        <button
                          className={`filter-option ${showCustomPicker || (searchParams.get('dateFrom') && !DATE_OPTIONS.some((o) => o.dateFrom === searchParams.get('dateFrom') && o.dateTo === searchParams.get('dateTo') && o.dateFrom)) ? 'selected' : ''}`}
                          onClick={() => setShowCustomPicker((v) => !v)}
                        >
                          Custom Dates <i className="fa-regular fa-calendar ml-1"></i>
                        </button>
                        {showCustomPicker && (
                          <div style={{ paddingTop: '8px' }}>
                            <DatePicker
                              selected={customFrom}
                              onChange={(dates) => {
                                const [start, end] = dates as [Date | null, Date | null];
                                setCustomFrom(start);
                                setCustomTo(end);
                              }}
                              startDate={customFrom}
                              endDate={customTo}
                              selectsRange
                              inline
                              monthsShown={2}
                              minDate={new Date()}
                              dateFormat="dd/MM/yyyy"
                            />
                            {customFrom && (
                              <p className="small text-muted mt-2 mb-1">
                                {fmtShort(customFrom)}
                                {customTo ? ` - ${fmtShort(customTo)}` : ' - select end date'}
                              </p>
                            )}
                            <div className="filter-dropdown-footer">
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => {
                                  setShowCustomPicker(false);
                                  setCustomFrom(null);
                                  setCustomTo(null);
                                  const params = new URLSearchParams(searchParams.toString());
                                  params.delete('dateFrom');
                                  params.delete('dateTo');
                                  params.delete('dateRange');
                                  params.delete('datePreset');
                                  params.set('page', '1');
                                  navigateFilter(`${basePath}?${params.toString()}`);
                                  setOpenDropdown(null);
                                }}
                              >
                                Clear
                              </button>

                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                  if (!customFrom || !customTo) return;
                                  const params = new URLSearchParams(searchParams.toString());
                                  params.set('dateFrom', ddmmyyyy(customFrom));
                                  params.set('dateTo', ddmmyyyy(customTo));
                                  params.delete('dateRange');
                                  params.delete('datePreset');
                                  params.set('page', '1');
                                  navigateFilter(`${basePath}?${params.toString()}`);
                                  setOpenDropdown(null);
                                  setShowCustomPicker(false);
                                }}
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* City Filter (single-select) */}
                {visibleFilters.includes('cityId') && (
                  <div className="position-relative">
                    <FilterButton dropKey="cityId" label={activeCityLabel()} />
                    {openDropdown === 'cityId' && (
                      <div className="filter-dropdown-panel">
                        <SingleOptionList
                          filterKey="cityId"
                          // Alphabetical by city name (spec 2026-08-09).
                          // Backend returns count-desc for the count
                          // badges; the dropdown itself reads better
                          // A→Z. `.slice()` first so the shared
                          // `filterOptions` array isn't mutated.
                          options={(filterOptions?.cities || [])
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((c) => ({
                              value: String(c.id),
                              label: `${c.name}${c.count ? ` (${c.count})` : ''}`,
                            }))}
                          // Cities outside the narrow result set (i.e. they have
                          // no events under the current filter combination)
                          // render as disabled — visible for context but not
                          // clickable. The wider list comes from
                          // `filterOptions.cities` which isn't filtered by
                          // user-applied selections.
                          enabledValues={matchingCityIds}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Artist Filter (multi-select) — the `participantId`
                    key is preserved on the URL / backend contract; only
                    the customer-facing label changes. On the sports
                    domain the same field surfaces as "Teams" — this
                    copy is concerts-specific and belongs here. */}
                {visibleFilters.includes('participantId') && (
                  <div className="position-relative">
                    <FilterButton
                      dropKey="participantId"
                      label={multiLabel('participantId', 'Artist')}
                    />
                    {openDropdown === 'participantId' && (
                      <div className="filter-dropdown-panel wide">
                        <MultiSelectPanel
                          items={filterOptions?.teams || []}
                          pending={pendingTeams}
                          onToggle={(id) => setPendingTeams(toggleInSet(pendingTeams, id))}
                          onClear={() => {
                            setPendingTeams(new Set());
                            updateMultiFilter('participantId', new Set());
                          }}
                          onApply={() => updateMultiFilter('participantId', pendingTeams)}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Team Type Filter (multi-select, team pages only) */}
                {visibleFilters.includes('teamType') && (
                  <div className="position-relative">
                    <FilterButton dropKey="teamType" label={multiLabel('teamType', 'Team Type')} />
                    {openDropdown === 'teamType' && (
                      <div className="filter-dropdown-panel">
                        <ul>
                          {TEAM_TYPE_OPTIONS.map((opt) => (
                            <li key={opt.value}>
                              <label className="filter-checkbox-item">
                                <input
                                  type="checkbox"
                                  checked={pendingTeamTypes.has(opt.value)}
                                  onChange={() =>
                                    setPendingTeamTypes(toggleInSet(pendingTeamTypes, opt.value))
                                  }
                                />
                                <span className="item-label">{opt.label}</span>
                              </label>
                            </li>
                          ))}
                        </ul>
                        <div className="filter-dropdown-footer">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setPendingTeamTypes(new Set());
                              updateMultiFilter('teamType', new Set());
                            }}
                          >
                            Clear
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => updateMultiFilter('teamType', pendingTeamTypes)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Venue Filter (multi-select) — rendered on every
                    concerts listing pageType EXCEPT `venue`
                    (`/concerts/venue/[slug]`), which is already
                    scoped to a single venue via `baseFilters`. Data
                    source is `filterOptions.venues` (optional on
                    the wire — the venue aggregate needs the
                    concerts backend rebuild; until then this
                    renders no items and the "Clear/Apply" panel is
                    empty). */}
                {visibleFilters.includes('venueId') && (
                  <div className="position-relative">
                    <FilterButton dropKey="venueId" label={multiLabel('venueId', 'Venue')} />
                    {openDropdown === 'venueId' && (
                      <div className="filter-dropdown-panel wide">
                        {/* Multi-select checkbox — the concerts
                            backend's `venueId` DTO now accepts a
                            CSV string (matching participantId /
                            tournamentId) and parses via
                            `parseIdList` + `$in`. Data source is
                            `effectiveVenues` (client-side derived
                            from a broad event scan when the
                            backend `filterOptions.venues` is
                            absent). */}
                        <MultiSelectPanel
                          items={effectiveVenues}
                          pending={pendingVenues}
                          onToggle={(id) => setPendingVenues(toggleInSet(pendingVenues, id))}
                          onClear={() => {
                            setPendingVenues(new Set());
                            updateMultiFilter('venueId', new Set());
                          }}
                          onApply={() => updateMultiFilter('venueId', pendingVenues)}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Price Filter (double range slider with Apply/Clear) */}
                {visibleFilters.includes('price') && (
                  <div className="position-relative">
                    <FilterButton dropKey="price" label={priceLabel()} />
                    {openDropdown === 'price' && (
                      <div className="filter-dropdown-panel wide no-scroll">
                        {(() => {
                          // Cross-domain remount race guard: `filterOptions`
                          // is fetched on mount (300ms debounced). Rendering
                          // the slider against the hardcoded 0..10000 GBP
                          // fallback while the fetch is in flight flashed a
                          // wrong range every time the user jumped
                          // sports↔concerts. Wait for a real payload before
                          // showing the slider — cheaper than a re-anchor
                          // flicker.
                          if (!filterOptions?.priceRange) {
                            return (
                              <div className="p-3 text-muted small">
                                Loading price range…
                              </div>
                            );
                          }
                          // Prices come back from the backend in GBP (sync fetches
                          // with currency=GBP). Convert min/max into the user's
                          // display currency so the slider labels are meaningful.
                          // filterOptions.priceRange is computed against the
                          // page-context query (no user-applied multi-selects),
                          // so the slider stays anchored to all events available
                          // for this page — selecting Football won't collapse the
                          // range and hide non-Football matches priced outside it.
                          const rawMax =
                            filterOptions.priceRange.max && filterOptions.priceRange.max > 0
                              ? filterOptions.priceRange.max
                              : 10000;
                          const rawMin =
                            filterOptions.priceRange.min && filterOptions.priceRange.min > 0
                              ? filterOptions.priceRange.min
                              : 0;
                          const sliderMax = Math.ceil(gbpToUser(rawMax));
                          const sliderMin = Math.floor(gbpToUser(rawMin));
                          // pendingMin/Max are stored in GBP (what the backend expects).
                          // Convert them to user currency for the slider's initial position.
                          const sliderInitMin = pendingMinPrice
                            ? Math.round(gbpToUser(Number(pendingMinPrice)))
                            : sliderMin;
                          const sliderInitMax = pendingMaxPrice
                            ? Math.round(gbpToUser(Number(pendingMaxPrice)))
                            : sliderMax;
                          return (
                            <PriceSlider
                              rangeMin={sliderMin}
                              rangeMax={sliderMax}
                              initialMin={sliderInitMin}
                              initialMax={sliderInitMax}
                              currencySymbol={currencySymbol}
                              onApply={(min, max) => {
                                // Convert slider values (user currency) → GBP for the API.
                                const minGbp = String(Math.floor(userToGbp(Number(min))));
                                const maxGbp = String(Math.ceil(userToGbp(Number(max))));
                                setPendingMinPrice(minGbp);
                                setPendingMaxPrice(maxGbp);
                                const params = new URLSearchParams(searchParams.toString());
                                params.set('minPrice', minGbp);
                                params.set('maxPrice', maxGbp);
                                params.set('page', '1');
                                navigateFilter(`${basePath}?${params.toString()}`);
                                setOpenDropdown(null);
                              }}
                              onClear={() => {
                                setPendingMinPrice('');
                                setPendingMaxPrice('');
                                const params = new URLSearchParams(searchParams.toString());
                                params.delete('minPrice');
                                params.delete('maxPrice');
                                params.set('page', '1');
                                navigateFilter(`${basePath}?${params.toString()}`);
                                setOpenDropdown(null);
                              }}
                            />
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sort button */}
              <div className="col-lg-3 col-md-12 text-lg-end">
                <div className="position-relative d-inline-block">
                  <button
                    type="button"
                    onClick={() => toggleDropdown('sortBy')}
                    disabled={filtersDisabled}
                    className={`sort-btn ${openDropdown === 'sortBy' ? 'open' : ''} ${filtersDisabled ? 'disabled' : ''}`}
                  >
                    Sorted by: {sortLabel()}
                    <i className="fa-sharp fa-light fa-arrow-down-arrow-up"></i>
                  </button>
                  {openDropdown === 'sortBy' && (
                    <div className="filter-dropdown-panel">
                      <ul>
                        {SORT_OPTIONS.map((opt) => {
                          const combined = sortState.sortBy
                            ? `${sortState.sortBy}:${sortState.sortOrder}`
                            : '';
                          return (
                            <li key={opt.value}>
                              <button
                                className={`filter-option ${combined === opt.value ? 'selected' : ''}`}
                                onClick={() => applySortOption(opt.value)}
                              >
                                {opt.label}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Active filter chips + Reset. The auto-resolved city
                chip is filtered out upstream on the concerts home
                (`hideCityChip`), so on first load `chips` is empty
                and this whole row stays hidden; the moment the user
                sets ANY other filter (dates / price / artist / venue)
                the chips (and Reset) appear as normal. */}
            {chips.length > 0 && (
              <div className="filter-chips">
                {chips.map((chip, i) => (
                  <span key={`${chip.key}-${chip.value || i}`} className="filter-chip">
                    {chip.label}
                    <button
                      type="button"
                      onClick={() => removeChip(chip.key, chip.value)}
                      className="chip-close"
                      aria-label="Remove"
                    >
                      <i className="fa-regular fa-xmark"></i>
                    </button>
                  </span>
                ))}
                <button type="button" onClick={clearFilters} className="clear-all-btn">
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
